/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { useRef } from "react";
import { Box } from "@material-ui/core";

import { useDisclosure } from "hooks/useDisclosure";
import { useIsChatAdmin } from "./useIsChatAdmin";
import { StreamingCommentBanDialog } from "./StreamingCommentBanDialog";

import type { UserMessage } from "@spwn/types/firebase/firestore";

export const CommentItemSuperChatV2: React.FC<{
  userIcon: string | undefined;
  userName: string;
  isCommentUser: boolean;
  amount: number;
  eventId: string;
  message: Pick<UserMessage, "_id" | "uid" | "msg">;
  backgroundColor: string;
  options: {
    color?: string;
    stickerImageUrl?: string;
    backgroundImageUrl?: string;
  };
  onClickBanUser?: (comment: Pick<UserMessage, "_id" | "uid">) => Promise<void>;
  onClickBanComment?: (id: string) => Promise<void>;
}> = ({
  userIcon,
  userName,
  isCommentUser,
  amount,
  eventId,
  message,
  backgroundColor,
  options = {},
  onClickBanUser,
  onClickBanComment,
}) => {
  const isChatAdmin = useIsChatAdmin(eventId);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const ref = useRef(null);
  const isEnabledBanDialog = isChatAdmin && !isCommentUser;

  return (
    <React.Fragment>
      {isEnabledBanDialog && onClickBanUser && onClickBanComment && (
        <StreamingCommentBanDialog
          target={ref}
          banTargetComment={isOpen ? message : undefined}
          disableUserBlock={isCommentUser}
          onClose={onClose}
          onClickBanUser={onClickBanUser}
          onClickBanComment={onClickBanComment}
        />
      )}
      <Box
        // @ts-expect-error TS2769
        ref={ref}
        css={css`
          display: grid;
          grid-template-columns: max-content 1fr max-content;
          align-items: center;
          padding: 8px;
          background-color: ${backgroundColor};
          border-radius: 12px;
          box-shadow:
            0 2px 2px 0 rgba(0, 0, 0, 0.14),
            0 1px 5px 0 rgba(0, 0, 0, 0.12),
            0 3px 1px -2px rgba(0, 0, 0, 0.2);
          background-image: url(${options.backgroundImageUrl});
          background-position: center;
          background-repeat: no-repeat;
          background-size: cover;
          &:not(:last-child) {
            margin-bottom: 8px;
          }
          ${isEnabledBanDialog
            ? `
          cursor: pointer;
          &:hover {
            opacity: 0.8;
          }`
            : undefined}
          ${isEnabledBanDialog && isOpen ? `opacity: 0.7;` : undefined}
        `}
        onClick={onOpen}
      >
        {/* 送信者アイコン */}
        <Box>
          <img
            src={userIcon}
            alt={userName}
            className="icon"
            css={css`
              display: block;
              width: 24px;
              height: 24px;
              background-color: gray;
              border-radius: 50%;
              aspect-ratio: 1 / 1;
              object-fit: cover;
            `}
          />
        </Box>

        {/* 送信者の情報 */}
        <Box paddingLeft={"8px"}>
          <Box
            css={css`
              color: ${options.color};
              font-size: 12px;
              font-weight: bold;
            `}
          >
            {userName}: {amount.toLocaleString()} PAW
          </Box>

          {message.msg && (
            <Box
              css={css`
                margin-top: 4px;
                color: ${options.color};
                font-size: 12px;
                line-height: 1.5em;
              `}
            >
              {message.msg}
            </Box>
          )}
        </Box>

        {/* ステッカーを表示する */}
        {options.stickerImageUrl ? (
          <Box paddingLeft={"8px"}>
            <img
              src={options.stickerImageUrl}
              alt=""
              css={css`
                width: 60px;
                aspect-ratio: 1 / 1;
                object-fit: cover;
              `}
            />
          </Box>
        ) : (
          // ギフト全体の高さを一定に保つため（ステッカーが有り無しで高さが変わらないようにする）ための空のBox。
          // このコンポーネントの一番親のBoxで調整しても良いが、固定値の説明が必要になるため、ここで調整するようにする。
          <Box height={"60px"} />
        )}
      </Box>
    </React.Fragment>
  );
};
